import React, { useState } from "react";
import { SITE } from "../lib/site";
import { Copy, Check, Landmark } from "lucide-react";
import { toast } from "sonner";

export default function Donate() {
  const [copied, setCopied] = useState(null);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success("Copied to clipboard", { description: text });
      setTimeout(() => setCopied(null), 1800);
    } catch (e) {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  return (
    <section
      id="donate"
      data-testid="donate-section"
      className="relative py-20 sm:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left intro */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#064E3B]">
              How to Donate
            </div>
            <div className="pill-divider mt-4" />
            <h2 className="font-serif-display text-3xl sm:text-5xl mt-6 leading-[1.1] text-[#111827] font-semibold">
              Give directly to our{" "}
              <span className="italic text-[#DC2626]">verified accounts.</span>
            </h2>
            <p className="mt-5 sm:mt-6 text-base sm:text-lg text-[#374151] leading-relaxed">
              Transfer your Zakatul Fitr, Zakatul Mall or Sadaqaat to either of the
              bank accounts below. After your transfer, kindly call or send your
              receipt to us so we can acknowledge your gift and direct it well.
            </p>
          </div>

          {/* Right — bank cards */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            {SITE.banks.map((b, i) => (
              <article
                key={b.name}
                data-testid={`bank-card-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="group relative overflow-hidden rounded-3xl border border-[#E5E0DA] bg-gradient-to-br from-white to-[#FAF5F0] p-6 sm:p-10 transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-[#064E3B]/5" />

                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 sm:h-12 sm:w-12 rounded-2xl bg-[#064E3B] text-white inline-flex items-center justify-center">
                      <Landmark size={20} strokeWidth={1.75} />
                    </div>
                    <h3 className="font-serif-display text-2xl sm:text-3xl text-[#111827] font-semibold">
                      {b.name}
                    </h3>
                  </div>
                  <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] font-semibold text-[#064E3B] bg-[#064E3B]/8 rounded-full px-2.5 py-1">
                    {i + 1}/{SITE.banks.length}
                  </span>
                </div>

                <div className="mt-7 sm:mt-8 grid sm:grid-cols-5 gap-5 items-end">
                  <div className="sm:col-span-3 min-w-0">
                    <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-semibold text-[#374151]">
                      Account Number
                    </div>
                    <div className="font-serif-display text-[2rem] sm:text-5xl text-[#111827] font-semibold mt-2 tracking-wider tabular-nums break-all">
                      {b.account}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <button
                      onClick={() => copy(b.account, b.name)}
                      data-testid={`copy-account-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#DC2626] hover:bg-[#B91C1C] text-white px-5 py-3 text-sm font-semibold transition-all hover:scale-[1.02]"
                    >
                      {copied === b.name ? (
                        <>
                          <Check size={16} /> Copied
                        </>
                      ) : (
                        <>
                          <Copy size={16} /> Copy Account
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div className="mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-[#E5E0DA] text-xs sm:text-sm text-[#374151]">
                  <span className="font-semibold text-[#111827]">Account name:</span>{" "}
                  {SITE.accountName}
                </div>
              </article>
            ))}

            <div className="rounded-3xl bg-[#064E3B] text-white p-8 relative overflow-hidden">
              <div className="absolute inset-0 geo-overlay-light opacity-40" />
              <div className="relative">
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#FCD34D]">
                  Reminder
                </div>
                <p className="mt-3 font-serif-display text-2xl leading-snug">
                  "The believer's shade on the Day of Resurrection will be their
                  charity."
                </p>
                <p className="mt-3 text-white/80 text-sm">— Prophet Muhammad ﷺ (al-Tirmidhi)</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
